import * as yup from 'yup';
import { HexColorPicker, setNonce } from 'react-colorful';
import { XCircle } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { EventHeader } from '../../../../common/Common/EventHeader';
import { ImageInput } from '../../../../common/Common/UploadButton';
import { Theme } from '../../../../theme';
import { defaultSecondary } from '../../../../theme/variables';
import { getServerErrors, hexColorRegex, resizedImageUrl } from '../../../../common/helpers';
import { getSufficientContrast } from '../../../../theme/helpers';
import PreviewCheckout, { PreviewCheckoutBackground } from './PreviewCheckout';
import ThemeProvider from '../../../../common/Common/ThemeProvider';
import UI from '../../../../common/UI';
import useDebouncedEffect from '../../../../common/useDebouncedEffect';
import useEditEventMutation from './useEditEventMutation';
import useMediaDevice from '../../../../common/useMediaDevice';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

setNonce(window.NONCE_ID);

const editEventBrandingSchema = yup.object({
  brand_color: yup.string().ensure().matches(hexColorRegex, {
    excludeEmptyString: true,
    name: 'hex_color',
  }),
});

export interface EditEventBrandingFormProps {
  event: Event;
}

const EditEventBrandingForm = ({ event }: EditEventBrandingFormProps) => {
  const { t } = useTranslation();
  const theme = useTheme() as Theme;

  const getValues = (event: Event) => ({
    brand_color: event.brand_color,
    logo: null as any,
    delete_logo: false,
    header: null as any,
    delete_header: false,
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      branding: form.values,
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventBrandingSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const validatedColor = (!errors.brand_color && form.values.brand_color) || defaultSecondary;
  const darkenedColor = getSufficientContrast(validatedColor, theme.getColor(theme.bodyBackground), 2);

  const [themeOptions, setThemeOptions] = useState({
    secondary: darkenedColor,
  });

  useDebouncedEffect(() => {
    setThemeOptions({ secondary: darkenedColor });
  }, 100, [validatedColor]);

  const device = useMediaDevice();

  const sportType = event.ticket_categories.find((ticketCategory) => !!ticketCategory.sport_type)?.sport_type;

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} attributes={{ 'branding.header': t('event_header') }} />

        <UI.FormGrid sc={{ columns: ['1fr', '250px 1fr'], gutter: [1, 2] }}>
          <UI.Div>
            <UI.FormGrid>
              <UI.Div>
                <UI.Legend sc={{ mb: 1 }}>{t('branding')}</UI.Legend>
                <UI.InputDescription>
                  {t('branding_description')}
                </UI.InputDescription>
              </UI.Div>

              <UI.InputGroup sc={{ valid: !errors.header, touched: form.touched.header }} style={{ maxWidth: '100%' }}>
                <UI.InputLabel>
                  <UI.Span sc={{ flex: { alignItems: 'center' } }}>
                    <UI.Span>
                      {t('event_header')}
                    </UI.Span>
                    <UI.Label
                      sc={{ background: 'secondary.400', color: 'white', ml: 1 }}
                      style={{ textTransform: 'uppercase', fontSize: 10 }}
                    >
                      {t('new')}
                    </UI.Label>
                  </UI.Span>
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('event_header_description')}
                </UI.InputDescription>
                <ImageInput
                  value={form.values.header}
                  deleteImage={form.values.delete_header}
                  onChange={(file) => {
                    form.set('header', file);
                    form.set('delete_header', !file);
                  }}
                  previewImage={event.header?.url}
                  imageAlt={event.title}
                  width={200}
                />
                <UI.ErrorMessages attribute={t('event_header')} errors={errors.header} />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.logo, touched: form.touched.logo }} style={{ maxWidth: '100%' }}>
                <UI.InputLabel>
                  {t('logo')}
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('logo_description')}
                </UI.InputDescription>
                <ImageInput
                  value={form.values.logo}
                  deleteImage={form.values.delete_logo}
                  onChange={(file) => {
                    form.set('logo', file);
                    form.set('delete_logo', !file);
                  }}
                  previewImage={event.logo?.url}
                  imageAlt={event.title}
                  width={200}
                />
                <UI.ErrorMessages attribute={t('logo')} errors={errors.logo} />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.brand_color, touched: form.touched.brand_color }}>
                <UI.InputLabel htmlFor="BrandColor">{t('brand_color')}</UI.InputLabel>
                <UI.InputWrapper style={{ width: 200 }}>
                  <UI.Input
                    {...form.text('brand_color')}
                    id="BrandColor"
                    placeholder={defaultSecondary}
                  />
                  {form.values.brand_color && (
                    <span>
                      <UI.A
                        onClick={() => form.set('brand_color', null)}
                        sc={{ color: 'gray.400' }}
                      >
                        <UI.Icon><XCircle /></UI.Icon>
                      </UI.A>
                    </span>
                  )}
                </UI.InputWrapper>
                <UI.ErrorMessages
                  attribute={t('brand_color')}
                  errors={errors.brand_color}
                />
                <UI.Div sc={{ mt: 2 }}>
                  <HexColorPicker
                    color={validatedColor || theme.colors.secondary[500]}
                    onChange={(color) => form.set('brand_color', color)}
                  />
                </UI.Div>
              </UI.InputGroup>
            </UI.FormGrid>
          </UI.Div>

          <UI.Div>
            <UI.Card sc={{ outline: 'gray.200', padding: 1 }}>
              <ThemeProvider options={themeOptions} key={themeOptions.secondary}>
                <PreviewCheckoutBackground sc={{ padding: [0, 4] }} style={{ borderRadius: 6 }}>
                  <UI.Div style={{ width: 400 }}>
                    <EventHeader
                      headerUrl={form.values.delete_header
                        ? null
                        : (form.values.header
                          ? URL.createObjectURL(form.values.header)
                          : (event.header ? resizedImageUrl(event.header.url, 1200) : null)
                        )}
                      sportType={sportType}
                      alt={event.title}
                    />

                    <UI.FlexContainer
                      sc={{ alignItems: 'center', relative: true, my: 4 }}
                      style={{ transform: device.isSmall && 'scale(0.85)' }}
                    >
                      {((event.logo && !form.values.delete_logo) || form.values.logo) && (
                        <UI.ProfilePic
                          sc={{ background: 'white', size: 36, mr: 1 }}
                        >
                          <img
                            src={form.values.logo
                              ? URL.createObjectURL(form.values.logo)
                              : (event.logo ? resizedImageUrl(event.logo.url, 300) : '')}
                            alt={event.title}
                          />
                        </UI.ProfilePic>
                      )}
                      <UI.H3 sc={{ fontSize: 3, strong: true }}>
                        {event.title}
                      </UI.H3>
                    </UI.FlexContainer>

                    <PreviewCheckout translationKey="default" />
                  </UI.Div>
                </PreviewCheckoutBackground>
              </ThemeProvider>
            </UI.Card>
          </UI.Div>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventBrandingForm;
